'use client';

import { useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { ProfileContext } from 'shared/misc/providers/ProfileContext';
import { gql } from 'shared/__generated__/gql';
import { useFragment, useQuery } from '@apollo/client';
import {
  GetProfileViewDocument,
  GetProfileViewQuery,
  ProfileThemeType,
  ProfileViewFragmentDoc,
  UserCoreFragmentDoc,
} from 'shared/__generated__/graphql';
import { useAuth } from 'shared/misc/hooks/useAuth';
import ProfileMainContent from './-profile-main-content';
import { useRouter } from '@tanstack/react-router';
import NotFound from '../../../components/not-found';

type ProfileViewProps = {
  username: string;
  initialProfileData: GetProfileViewQuery['user'];
};

gql(/* GraphQL */ `
  fragment ProfileView on User {
    ...UserCore
    isViewerBlocked
    isBlockedByViewer
    bio
    bioLexical
    profilePhotoSrc
    relationshipStatus
    interestedIn
    profilePhotoFile {
      ...PerfImageFromFile
    }
    avatarPhotoSrc
    amIFollowing
    areTheyFollowing
    followingCount
    followersCount
    recCount
    promptCount
    customTheme {
      colors
      updatedAt
    }
    profileTheme
    recs(first: 5, includePromptRecs: true) {
      id
      emoji
    }
    # listCount
    # lists(orderBy: "recentlyUpdated") {
    #   ...ListItemSmall
    # }
  }
`);

gql(/* GraphQL */ `
  query getProfileView($username: String!) {
    user(username: $username) {
      ...ProfileView
    }
  }
`);

export default function ProfileView({ username, initialProfileData }: ProfileViewProps) {
  const auth = useAuth();
  const router = useRouter();

  const { data: upToDateData, error } = useQuery(GetProfileViewDocument, {
    variables: { username },
    initialFetchPolicy: 'cache-first',
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const { complete, data: fragmentData } = useFragment({
    fragment: UserCoreFragmentDoc,
    fragmentName: 'UserCore',
    from: {
      __typename: 'User',
      id: initialProfileData?.id,
    },
  });

  let profile = upToDateData?.user
    ? upToDateData.user
    : complete
    ? fragmentData
    : initialProfileData;

  if (!profile?.id && initialProfileData?.id) {
    profile = initialProfileData;
  }

  const memoizedProfile = useMemo(() => ({ 
    profile, 
    // theme: theme || profile?.profileTheme || null,
    // customTheme 
  // }), [profile, theme, customTheme]);
  }), [profile]);

  if (
    !profile?.id ||
    (auth.user && upToDateData?.user?.isShadowBanned && auth.user?.id !== profile?.id)
  ) {
    return <NotFound />;
  }

  if (!profile?.id) {
    console.log('no profile');
    return null;
  }

  return (
    <ProfileContext.Provider value={memoizedProfile}>
      <Box overflow="visible" maxWidth="100%">
        <ProfileMainContent />
      </Box>
    </ProfileContext.Provider>
  );
}
